import camelcaseKeys from 'camelcase-keys';
import moment from 'moment';

import { call, put } from 'redux-saga/effects';

import api from '../../api/navarino';
import {
  SERVICE_WHITELIST, VALUE_HOTEL_CLOSED, VALUE_ID_CONFIRMATION, VALUE_ID_CANCELLATION,
} from '../../constants/api';
import {
  createPurchaseContext, trackException, trackPurchase, trackRefund,
} from '../../helpers/analytics';

import {
  ADD_ROOM_ALLOWED,
  ADD_ROOM_DENIED,
  CANCEL_BOOKING_FAILED,
  CANCEL_BOOKING_RATE_LIMITED,
  CANCEL_BOOKING_SUCCEEDED,
  FETCH_AVAILABILITY_FAILED,
  FETCH_AVAILABILITY_SUCCEEDED,
  FETCH_ADDITIONAL_AVAILABILITY_FAILED,
  FETCH_ADDITIONAL_AVAILABILITY_SUCCEEDED,
  FETCH_BOOKING_FAILED,
  FETCH_BOOKING_NOT_FOUND,
  FETCH_BOOKING_RATE_LIMITED,
  FETCH_BOOKING_SUCCEEDED,
  FETCH_GENERAL_AVAILABILITY_FAILED,
  FETCH_GENERAL_AVAILABILITY_SUCCEEDED,
  FETCH_INFO_FAILED,
  FETCH_INFO_SUCCEEDED,
  FETCH_SERVICES_FAILED,
  FETCH_SERVICES_SUCCEEDED,
  MAKE_BOOKING_FAILED,
  MAKE_BOOKING_RATE_LIMITED,
  MAKE_BOOKING_SUCCEEDED,
} from '../actionTypes';


function groupServicesByCode(services) {
  const groupedServices = services
    .filter((s) => SERVICE_WHITELIST.includes(s.serviceInventoryCode))
    .reduce((acc, s) => {
      if (!acc[s.serviceInventoryCode]) {
        acc[s.serviceInventoryCode] = s;
      } else if (s.serviceDetails?.timeSpan) {
        const { end, start } = s.serviceDetails.timeSpan;
        const current = acc[s.serviceInventoryCode];
        if (start < current.serviceDetails.timeSpan.start) {
          acc[s.serviceInventoryCode].serviceDetails.timeSpan.start = start;
        }
        if (end > current.serviceDetails.timeSpan.end) {
          acc[s.serviceInventoryCode].serviceDetails.timeSpan.end = end;
        }
      }
      return acc;
    }, {});
  return Object.values(groupedServices);
}

function groupServicesByCodeUsingAttributes(services) {
  const groupedServices = services
    .reduce((acc, s) => {
      if (!acc[s.attributes.serviceInventoryCode]) {
        acc[s.attributes.serviceInventoryCode] = s;
      } else if (s.serviceDetails?.timeSpan) {
        const { end, start } = s.serviceDetails.timeSpan.attributes;
        const current = acc[s.attributes.serviceInventoryCode];
        if (start < current.serviceDetails.timeSpan.attributes.start) {
          acc[s.attributes.serviceInventoryCode].serviceDetails.timeSpan.attributes.start = start;
        }
        if (end > current.serviceDetails.timeSpan.attributes.end) {
          acc[s.attributes.serviceInventoryCode].serviceDetails.timeSpan.attributes.end = end;
        }
      }
      return acc;
    }, {});
  return Object.values(groupedServices);
}

function parseBooking(booking) {
  const roomStay = booking.roomStays?.roomStay[0];

  const { hotelCode } = roomStay.basicPropertyInfo;
  const { start, end } = roomStay.timeSpan;
  const { guestCount } = roomStay.guestCounts;
  const { numberOfUnits, ratePlanCode, roomTypeCode } = roomStay.roomRates.roomRate[0];
  const { amountAfterTax, currencyCode } = roomStay.total;

  const guestNums = guestCount.sort((a, b) => b.ageQualifyingCode - a.ageQualifyingCode);

  const ratePlan = roomStay.ratePlans.ratePlan[0];
  const { guarantee, cancelPenalties: { cancelPenalty } } = ratePlan;
  const { ratePlanName } = ratePlan;
  const ratePlanDescription = ratePlan.additionalDetails.additionalDetail[0].detailDescription.text;

  const roomType = roomStay.roomTypes.roomType[0];
  const roomTypeName = roomType.roomDescription.name;

  const stay = {
    rate: {
      plan: ratePlanCode,
      name: ratePlanName,
      desc: ratePlanDescription,
    },
    ratePlan: ratePlanCode,
    roomType: roomTypeCode,
    currency: currencyCode,
    total: amountAfterTax,
    arrivalDate: start,
    duration: moment(end).diff(moment(start), 'days'),
    adults: parseInt(guestNums[0].count, 10),
    children: guestNums.length > 1 ? parseInt(guestNums[1].count, 10) : 0,
    numRooms: parseInt(numberOfUnits, 10),
    roomTypeName,
    ratePlanName,
  };

  const { customer } = booking.resGuests?.resGuest[0].profiles?.profileInfo[0].profile;
  const guestDetails = {
    name: customer.personName[0].surname,
    telephone: customer.telephone[0].phoneNumber,
    email: customer.email[0],
    addressLines: customer.address[0].addressLine,
    addressCity: customer.address[0].cityName,
    postalCode: customer.address[0].postalCode,
  };

  const { cardNumber } = booking.resGlobalInfo?.guarantee?.guaranteesAccepted?.guaranteeAccepted[0].paymentCard;

  const services = booking.services?.service || [];

  const { hotelReservationId } = booking.resGlobalInfo?.hotelReservationIDs;
  const confirmationId = hotelReservationId.find((id) => id.resIdType === VALUE_ID_CONFIRMATION);
  const cancellationId = hotelReservationId.find((id) => id.resIdType === VALUE_ID_CANCELLATION);

  const cancelPolicy = {
    code: cancelPenalty[0].policyCode,
    text: cancelPenalty[0].penaltyDescription[0].text,
  };

  const guaranteePolicy = {
    code: guarantee.guaranteeCode,
    text: guarantee.guaranteeDescription.text,
  };

  return {
    confirmationId: confirmationId.resIdValue,
    cancellationId: cancellationId?.resIdValue,
    hotelCode,
    guestDetails,
    cancelPolicy,
    guaranteePolicy,
    services: groupServicesByCode(services),
    stay,
    creditCardSuffix: cardNumber ? cardNumber.substr(cardNumber.length - 4, 4) : null,
    isCancellable: booking.tpaExtensions.resStatus.isCancellable === 'true',
  };
}

// What the confirmation page reads when the reply could not be parsed in
// full: it renders guest details and the guarantee code unguarded, so these
// must be objects, not null. The cancel policy block is guarded and may be
// null.
const EMPTY_GUEST_DETAILS = Object.freeze({
  name: '', telephone: '', email: '', addressLines: [], addressCity: '', postalCode: '',
});
const EMPTY_POLICY = Object.freeze({ code: '', text: '' });

// The booking number and the stay are what a booking is; everything
// descriptive (guest profile, policies, rate and room names) defaults to
// empty when Navarino leaves it out or reshapes it, so a cosmetic gap in the
// reply never turns into the failure screen for a stay the hotel has taken.
function parseReservation(reservation) {
  const guest = reservation.guests?.[0]?.profiles?.profileInfo?.['0']?.profile?.customer || {};
  const address = guest.address?.['0'] || {};
  const guestDetails = {
    name: guest.personName?.['0']?.surname || '',
    telephone: guest.telephone?.['0']?.phoneNumber || '',
    email: guest.email?.['0'] || '',
    addressLines: Object.values(address.addressLine || {}),
    addressCity: address.cityName || '',
    postalCode: address.postalCode || '',
  };

  const roomStay = reservation.stays[0];
  const cancelPenalty = roomStay.cancelPenalties?.cancelPenalty?.['0'];
  const cancelPolicy = cancelPenalty ? {
    code: cancelPenalty.policyCode || '',
    text: cancelPenalty.penaltyDescription?.['0']?.text || '',
  } : null;
  const guaranteePolicy = {
    code: roomStay.guarantee?.guaranteeCode || '',
    text: roomStay.guarantee?.guaranteeDescription?.text || '',
  };

  const { start, end } = roomStay.timeSpan;
  const { guestCount } = roomStay.guestCounts;
  const guestNums = Object.values(guestCount).sort((a, b) => parseInt(b.ageQualifyingCode, 10) - parseInt(a.ageQualifyingCode, 10));

  const { numberOfUnits, ratePlanCode, roomTypeCode } = roomStay.roomRates.roomRate[0];
  const ratePlan = roomStay.ratePlans?.ratePlan?.[0] || {};
  const ratePlanName = ratePlan.ratePlanName || '';
  const ratePlanDescription = ratePlan.additionalDetails?.additionalDetail?.[0]?.detailDescription?.text || '';

  const roomTypeName = roomStay.roomTypes?.roomType?.[0]?.roomDescription?.name || '';

  const stay = {
    rate: {
      plan: ratePlanCode,
      name: ratePlanName,
      desc: ratePlanDescription,
    },
    ratePlan: ratePlanCode,
    roomType: roomTypeCode,
    total: parseFloat(reservation.total),
    // parseBooking carries a currency and this parser did not, so the purchase
    // path fell back to a hardcoded EUR in three separate helpers.
    currency: reservation.currency || 'EUR',
    arrivalDate: start,
    duration: moment(end).diff(moment(start), 'days'),
    adults: parseInt(guestNums[0].count, 10),
    children: guestNums.length > 1 ? parseInt(guestNums[1].count, 10) : 0,
    numRooms: parseInt(numberOfUnits, 10),
    roomTypeName,
    ratePlanName,
  };

  const services = reservation.services || [];

  return {
    confirmationId: reservation.id,
    stay,
    services: groupServicesByCodeUsingAttributes(services),
    guestDetails,
    cancelPolicy,
    guaranteePolicy,
  };
}

// Fallback when even the strict part of parseReservation throws: the hotel
// has the booking, so the guest gets the confirmation page with the booking
// number, and the reducer keeps the stay, details and services it already
// holds for that page.
const minimalReservation = (reservation) => ({
  confirmationId: (reservation && reservation.id) || '',
  stay: null,
  services: [],
  guestDetails: { ...EMPTY_GUEST_DETAILS },
  cancelPolicy: null,
  guaranteePolicy: { ...EMPTY_POLICY },
});

function staysToObject(stays) {
  return stays.map((stay) => ([stay.roomType, stay.ratePlan, stay]))
    .reduce((collection, [roomType, ratePlan, stay]) => {
      if (collection[roomType]) {
        collection[roomType][ratePlan] = stay;
      } else {
        collection[roomType] = {
          [ratePlan]: stay,
        };
      }
      return collection;
    }, {});
}

function infoToMaxOccupancy(rooms) {
  return rooms
    .reduce((acc, r) => {
      acc[r.code] = parseInt(r.maxOccupancy, 10);
      return acc;
    }, {});
}

function availabilityToArray(stays) {
  return stays.filter((s) => s.status === VALUE_HOTEL_CLOSED)
    .map((s) => moment(s.arrivalDate));
}

const isPromoCodeValid = (promoCode, warnings) => (!!promoCode && (!warnings || !warnings.find((w) => w.includes(`Invalid Promotion Code: ${promoCode}`))));

export function* fetchGeneralAvailability(action) {
  try {
    const { data, error } = yield call(api.fetchGeneralAvailability, action.payload);
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const blockedDays = result.hotelStays ? availabilityToArray(result.hotelStays) : [];
      yield put({ type: FETCH_GENERAL_AVAILABILITY_SUCCEEDED, blockedDays });
    } else {
      throw error;
    }
  } catch (error) {
    // Not reported here: this runs on every page load and is retried by
    // calendarSaga, which reports once if all attempts fail. Per-attempt
    // reporting turned Safari bounces and link prefetches into hundreds of
    // Sentry events a day.
    yield put({ type: FETCH_GENERAL_AVAILABILITY_FAILED, error: String(error) });
  }
}

export function* fetchHotelInfo() {
  try {
    const { data, error } = yield call(api.fetchHotelInfo);
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const contents = result.hotelDescriptiveContent.length && result.hotelDescriptiveContent[0];
      const rooms = contents?.facilityInfo?.guestRooms?.guestRoom;
      const maxOccupancy = rooms ? infoToMaxOccupancy(rooms) : {};
      yield put({ type: FETCH_INFO_SUCCEEDED, maxOccupancy });
    } else {
      throw error;
    }
  } catch (error) {
    // Same as fetchGeneralAvailability: infoSaga reports after its retries.
    yield put({ type: FETCH_INFO_FAILED, error: String(error) });
  }
}

export function* fetchAdditionalAvailability(action) {
  try {
    const { data, error } = yield call(api.fetchAvailability, action.payload);
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      yield put({ type: FETCH_ADDITIONAL_AVAILABILITY_SUCCEEDED });
      const isAllowed = result.stays?.stay && result.stays.stay.length > 0;
      yield put({ type: isAllowed ? ADD_ROOM_ALLOWED : ADD_ROOM_DENIED });
    } else {
      throw error;
    }
  } catch (error) {
    yield call(trackException, `bookpoint: ${error}`, true);
    yield put({ type: FETCH_ADDITIONAL_AVAILABILITY_FAILED });
  }
}

export function* fetchAvailability(action) {
  const { promoCode } = action.payload;
  try {
    const { data, error } = yield call(api.fetchAvailability, action.payload);
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const stays = result.stays?.stay ? staysToObject(result.stays.stay) : {};
      const effect = { type: FETCH_AVAILABILITY_SUCCEEDED, stays };
      if (promoCode) effect.promoCodeValid = isPromoCodeValid(promoCode, result.warnings);
      yield put(effect);
    } else {
      throw error;
    }
  } catch (error) {
    yield call(trackException, `bookpoint: ${error}`, true);
    yield put({ type: FETCH_AVAILABILITY_FAILED });
  }
}

export function* fetchServices(action) {
  try {
    const { data, error } = yield call(api.fetchServices, action.payload);
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const services = result.services?.service || [];
      yield put({ type: FETCH_SERVICES_SUCCEEDED, services: groupServicesByCode(services) });
    } else {
      throw error;
    }
  } catch (error) {
    yield call(trackException, `bookpoint: ${error}`, true);
    yield put({ type: FETCH_SERVICES_FAILED });
  }
}

// Upstream answered with JSON but no reservation (declined card, validation
// error, a literal null reply). `error` is undefined on that path, so name
// the reply shape instead of throwing undefined. Only the top-level keys
// travel to analytics, sorted and capped so one shape is one Sentry issue;
// the proxy reports a sanitised body server-side.
const REJECTION_KEYS_MAX = 5;
const bookingRejection = (data) => {
  const shape = data && typeof data === 'object'
    ? (Object.keys(data).sort().slice(0, REJECTION_KEYS_MAX).join(',') || 'empty object')
    : 'null reply';
  return new Error(`rejected (${shape})`);
};

export function* makeBookingRequest(action) {
  try {
    /* Captured before the request so the browser Purchase and the server-side
       Conversions API event share one event_id and Meta collapses the pair
       instead of counting two. Obtained as an effect, not called directly, so
       the random id stays injectable in the saga tests. */
    const tracking = yield call(createPurchaseContext);
    const { data, error } = yield call(api.makeBooking, { ...action.payload, eventId: tracking.eventId });
    // Proxy envelopes (429 / 503) parse as JSON, so they land on `data`. Check
    // before the truthiness branch below — otherwise the destructure of
    // data.reservations would throw. Only 429 is a rate-limit the user caused;
    // 503 means our limiter backend is degraded and must surface as the
    // generic retry error, not a "too many attempts" accusation.
    if (data && (data.error === 'rate_limited' || data.error === 'service_unavailable')) {
      const fatal = data.error === 'service_unavailable';
      yield call(trackException, `bookpoint: book ${data.error}`, fatal);
      yield put({ type: fatal ? MAKE_BOOKING_FAILED : MAKE_BOOKING_RATE_LIMITED });
      return;
    }
    if (data && Array.isArray(data.reservations) && data.reservations.length) {
      const result = camelcaseKeys(data, { deep: true });
      let reservation;
      let parsedInFull = true;
      try {
        reservation = parseReservation(result.reservations[0]);
      } catch (parseError) {
        // Same rule as tracking below: a reply we cannot read must not show
        // the failure screen for a booking Navarino has taken. Confirm with
        // the booking number and let Sentry carry the path that was missing.
        parsedInFull = false;
        yield call(trackException, `bookpoint: book parse ${parseError}`, true);
        reservation = minimalReservation(result.reservations[0]);
      }

      /* One effect for the whole purchase: GA4 purchase, the Google Ads
         conversion and the Meta Purchase. It used to be three separate calls
         reporting the same booking, one of which was not an effect at all and
         so was invisible to these tests.

         The inner catch is the structural fix for the incident where a booking
         succeeded upstream but the guest saw the failure screen: a throw in
         tracking fell through to the outer catch and dispatched
         MAKE_BOOKING_FAILED for a stay the hotel had already taken. Tracking
         must never decide whether a booking happened. */
      try {
        if (parsedInFull) {
          yield call(trackPurchase, {
            reservation,
            services: action.payload.services,
            source: action.payload.stay && action.payload.stay.source,
            eventId: tracking.eventId,
          });
        }
      } catch (trackingError) {
        yield call(trackException, `tracking: purchase ${trackingError}`, false);
      }

      yield put({ type: MAKE_BOOKING_SUCCEEDED, reservation });
    } else {
      throw error || bookingRejection(data);
    }
  } catch (error) {
    yield call(trackException, `bookpoint: book ${error}`, true);
    yield put({ type: MAKE_BOOKING_FAILED });
  }
}

export function* fetchBooking(action) {
  try {
    const { data, error } = yield call(api.fetchBooking, action.payload);
    // 429 and 404 bodies still parse as JSON and land on `data`, so they
    // must be checked before the truthiness branch. The proxy validates the
    // email server-side; this saga only reacts to the `not_found` /
    // `rate_limited` envelopes it returns.
    if (data && (data.error === 'rate_limited' || data.error === 'service_unavailable')) {
      const fatal = data.error === 'service_unavailable';
      yield call(trackException, `bookpoint: read ${data.error}`, fatal);
      yield put({ type: fatal ? FETCH_BOOKING_FAILED : FETCH_BOOKING_RATE_LIMITED });
      return;
    }
    if (data && data.error === 'not_found') {
      // Track at non-fatal severity so a 404 surge (enumeration attempt or a
      // shape/check regression that silently 404s real guests) is visible
      // alongside real not-founds — analytics can split by volume.
      yield call(trackException, 'bookpoint: read not_found', false);
      yield put({ type: FETCH_BOOKING_NOT_FOUND });
      return;
    }
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const booking = result.hotelReservations?.hotelReservation[0];
      if (booking) {
        const reservation = parseBooking(booking);
        yield put({ type: FETCH_BOOKING_SUCCEEDED, reservation });
      } else {
        yield put({ type: FETCH_BOOKING_NOT_FOUND });
      }
    } else {
      throw error;
    }
  } catch (error) {
    yield call(trackException, `bookpoint: ${error}`, true);
    yield put({ type: FETCH_BOOKING_FAILED });
  }
}

export function* cancelBooking(action) {
  try {
    const { data, error } = yield call(api.cancelBooking, action.payload);
    if (data && (data.error === 'rate_limited' || data.error === 'service_unavailable')) {
      const fatal = data.error === 'service_unavailable';
      yield call(trackException, `bookpoint: cancel ${data.error}`, fatal);
      yield put({ type: fatal ? CANCEL_BOOKING_FAILED : CANCEL_BOOKING_RATE_LIMITED });
      return;
    }
    if (data) {
      const result = camelcaseKeys(data, { deep: true });
      const { cancelInfoRs: { uniqueId: { id } } } = result;
      yield put({ type: CANCEL_BOOKING_SUCCEEDED, payload: { confirmationId: id } });

      /* A cancelled booking has to reverse its conversion, or the paid
         channels keep the credit for revenue that never happened. Guarded for
         the same reason the purchase is: this runs after the cancellation has
         already succeeded, and must not be able to turn it into a failure. */
      if (action.refund) {
        try {
          yield call(trackRefund, action.refund);
        } catch (trackingError) {
          yield call(trackException, `tracking: refund ${trackingError}`, false);
        }
      }
    } else {
      throw error;
    }
  } catch (error) {
    yield call(trackException, `bookpoint: ${error}`, true);
    yield put({ type: CANCEL_BOOKING_FAILED });
  }
}
